import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { mapValues } from 'lodash';

import { setCarouselPageIsCompletedAction } from 'src/components/shared/SciPage/SciPage.duck';
import { SciPagesEnum } from 'src/components/shared/SciPage/types';

import {
    productsAreLoadingSelector,
    extrasSelector,
} from '../shared/products.selector';
import { productGetAction, productUpdateAction } from '../shared/products.duck';
import { UpdateProductPayload } from '../shared/types';

import { ExtraProduct, Extras } from './types';

const mapExtrasToForm = (extras: Extras) =>
    Object.keys(extras).reduce(
        (acc, extraCategoryKey: string) => ({
            ...acc,
            ...extras[extraCategoryKey].products.reduce(
                (accProduct, p: ExtraProduct) => ({
                    ...accProduct,
                    [p.id.productId]: p.quantity,
                }),
                {},
            ),
        }),
        {},
    );

const mapFormToProductPayload = (
    productId: string,
    quantity: number,
    extras: Extras,
): UpdateProductPayload | null => {
    const product: ExtraProduct | undefined = Object.keys(extras).reduce(
        (acc: ExtraProduct | undefined, extraCategoryKey: string) => {
            if (acc) {
                return acc;
            }

            const extraCategory = extras[extraCategoryKey];
            return extraCategory.products.find(
                (p) => p.id.productId === productId,
            );
        },
        undefined,
    );

    if (product) {
        return {
            id: product.id,
            uniqueProductId: product.uniqueProductId,
            quantity: quantity,
        };
    }
    return null;
};

export const useExtrasForm = () => {
    const dispatch = useDispatch();
    const isLoading = useSelector(productsAreLoadingSelector);
    const extras = useSelector(extrasSelector);

    const schema = yup.lazy((obj) =>
        yup.object(mapValues(obj, () => yup.number())),
    );

    const { handleSubmit, control, watch, reset, getValues } = useForm({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        const subscription = watch((data, info) => {
            const isManualChange = !!info?.name;

            if (isManualChange) {
                const productId = info.name;

                if (!productId) {
                    return;
                }

                const quantity = data[productId];

                const mappedProductPayload: UpdateProductPayload | null =
                    mapFormToProductPayload(productId, quantity, extras);

                if (mappedProductPayload) {
                    dispatch(productUpdateAction(mappedProductPayload));
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, dispatch, extras]);

    useEffect(() => {
        // Reset value once getting response from BE
        const newValues = mapExtrasToForm(extras);
        reset({ ...newValues });
    }, [extras, reset, getValues]);

    useEffect(() => {
        dispatch(productGetAction());
    }, [dispatch]);

    const onSubmit = () => {
        dispatch(
            setCarouselPageIsCompletedAction({
                page: SciPagesEnum.Extras,
            }),
        );
    };

    return {
        control,
        isLoading,
        extras,
        onSubmit: handleSubmit(onSubmit),
    };
};
